/**
 * Version history drawer (FEAT-2026-0005, Phase 2).
 *
 * Right-side drawer listing every version with one-click View / Restore /
 * Compare actions. The Compare dialog is opened with the current version
 * as the right pane and the chosen version as the left pane.
 */

import { useEffect, useState } from 'react';
import {
  Drawer, Box, Stack, Typography, Chip, Button, IconButton, Divider,
  Tooltip, CircularProgress, Alert,
} from '@mui/material';
import { Close, RestoreOutlined, CompareArrowsOutlined, VisibilityOutlined } from '@mui/icons-material';
import { reportTemplateService, type VersionWithMetadata } from '../../../services/reportTemplateService';

interface Props {
  open: boolean;
  onClose: () => void;
  templateKey: string;
  /** Currently active version number; styled differently in the list. */
  currentVersionNumber: number | null;
  /** Called when the user restores or views a version (so the parent can
   *  refresh the editor state). */
  onAfterRestore: () => void;
  onViewVersion: (versionNumber: number) => void;
  onCompareVersion: (versionNumber: number) => void;
}

export const VersionHistoryDrawer = ({
  open, onClose, templateKey, currentVersionNumber,
  onAfterRestore, onViewVersion, onCompareVersion,
}: Props) => {
  const [versions, setVersions] = useState<VersionWithMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportTemplateService.listVersions(templateKey)
      .then((rows) => { if (!cancelled) setVersions(rows); })
      .catch((err) => { if (!cancelled) setError(err?.message ?? String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, templateKey]);

  const handleRestore = async (versionNumber: number) => {
    if (!window.confirm(`Restore v${versionNumber} as the new active version? A new version (v${(versions[0]?.version.VersionNumber ?? 0) + 1}) will be created with this content.`)) return;
    setRestoring(versionNumber);
    try {
      await reportTemplateService.restore(templateKey, versionNumber);
      onAfterRestore();
      // Refresh the list so the new version appears at the top.
      const rows = await reportTemplateService.listVersions(templateKey);
      setVersions(rows);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? String(err));
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: 480, maxWidth: '95vw' } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ flex: 1, fontSize: '1rem', fontWeight: 600 }}>Version history</Typography>
        <IconButton onClick={onClose} size="small"><Close fontSize="small" /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>}

      <Stack divider={<Divider />} sx={{ flex: 1, overflow: 'auto' }}>
        {versions.map((v) => {
          const n = v.version.VersionNumber;
          const isCurrent = n === currentVersionNumber;
          const isFactory = !!v.version.IsFactory;
          return (
            <Box key={v.version.ReportTemplateVersionKey} sx={{
              p: 2, position: 'relative',
              backgroundColor: isCurrent ? 'action.selected' : undefined,
            }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.95rem', fontWeight: 700 }}>v{n}</Typography>
                {isCurrent && <Chip size="small" label="current" color="primary" sx={{ height: 20 }} />}
                {isFactory && <Chip size="small" label="factory" variant="outlined" sx={{ height: 20 }} />}
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {new Date(v.version.PublishedAt).toLocaleString()}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Published by {v.version.PublishedBy}
              </Typography>
              {v.version.ChangeNote && (
                <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', fontSize: '0.85rem' }}>
                  {v.version.ChangeNote}
                </Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Tooltip title="View this version's config">
                  <Button size="small" startIcon={<VisibilityOutlined fontSize="small" />} onClick={() => onViewVersion(n)}>
                    View
                  </Button>
                </Tooltip>
                <Tooltip title="Compare against the current version">
                  <Button size="small" startIcon={<CompareArrowsOutlined fontSize="small" />} onClick={() => onCompareVersion(n)}>
                    Compare
                  </Button>
                </Tooltip>
                {!isCurrent && (
                  <Tooltip title="Make this the active version">
                    <Button size="small" startIcon={<RestoreOutlined fontSize="small" />}
                      disabled={restoring === n}
                      onClick={() => handleRestore(n)}>
                      Restore
                    </Button>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          );
        })}
        {!loading && versions.length === 0 && !error && (
          <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            No versions yet.
          </Typography>
        )}
      </Stack>
    </Drawer>
  );
};
